import { useTranslation } from 'react-i18next';
import { Badge, Button, ButtonFooter, Section, SectionHeader } from 'components/ds';
import { IcHourglass } from 'components/common/icons/EmojiIcons';
import styles from './ScheduleMainContent.module.css';
import { toLocalISOString } from 'common/utils/datetime';

interface Schedule {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
}

interface Participant {
    id: string;
    name: string;
    avatar: string;
    schedules: Schedule[];
}

interface TimeSlot {
    date: string;
    startTime: string;
    endTime: string;
    availableCount: number;
    voteCount: number;
    proposers: any[];
    voters: string[];
    isRecommended: boolean;
    hasUserVoted: boolean;
}

interface ScheduleMainContentProps {
    participants: Participant[];
    timeSlots: TimeSlot[];
    aiRecommendations: any[];
    onAddSchedule: () => void;
    onVote: () => void;
}

export const ScheduleMainContent = ({
    participants,
    timeSlots,
    aiRecommendations,
    onAddSchedule,
    onVote
}: ScheduleMainContentProps) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR';
    const weekDaysT = t('common.weekDaysShort', { returnObjects: true }) as string[];
    // 일정을 제출한 참여자 수 계산
    const submittedCount = participants.filter(p => p.schedules && p.schedules.length > 0).length;
    const totalCount = participants.length;

    // 날짜 포맷팅 (YYYY-MM-DD → locale)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return t('common.monthDay', { month: date.getMonth() + 1, day: date.getDate() });
    };

    // 요일 표시
    const getDayOfWeek = (dateString: string) => {
        const date = new Date(dateString);
        return weekDaysT[date.getDay()];
    };

    // 투표 수 기준으로 정렬 (많은 순서대로)
    const sortedTimeSlots = [...timeSlots].sort((a, b) => {
        // 투표 수로 먼저 정렬 (내림차순)
        if (b.voteCount !== a.voteCount) {
            return b.voteCount - a.voteCount;
        }
        // 투표 수가 같으면 제안 수로 정렬 (내림차순)
        if (b.availableCount !== a.availableCount) {
            return b.availableCount - a.availableCount;
        }
        // 그것도 같으면 날짜/시간 순서대로 (오름차순)
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.startTime.localeCompare(b.startTime);
    });

    return (
        <>
            {/* 얼마나 모였는지 */}
            <Section spacing="tight">
                <SectionHeader
                    title={t('scheduleMainContent.progressTitle')}
                    sub={t('scheduleMainContent.progressDesc')}
                />
                <div className={styles.progressBar}>
                    <span
                        className={styles.progressFill}
                        style={{ width: totalCount > 0 ? `${(submittedCount / totalCount) * 100}%` : '0%' }}
                    />
                </div>
                <div className={styles.progressStats}>
                    <span>{t('scheduleMainContent.submittedCount', { submitted: submittedCount, total: totalCount })}</span>
                    <strong>{totalCount > 0 ? Math.round((submittedCount / totalCount) * 100) : 0}%</strong>
                </div>
            </Section>

            {/* 투표 현황 */}
            {sortedTimeSlots.length > 0 && (
                <Section divided>
                    <SectionHeader
                        title={t('scheduleMainContent.voteStatus')}
                        sub={t('scheduleMainContent.votePrompt')}
                    />
                    <ul className={styles.slots}>
                        {sortedTimeSlots.slice(0, 5).map((slot, index) => (
                            <li key={index} className={styles.slot}>
                                <span className={styles.slotWhen}>
                                    {formatDate(slot.date)} ({getDayOfWeek(slot.date)}){' '}
                                    {slot.startTime.substring(0, 5)} ~ {slot.endTime.substring(0, 5)}
                                </span>
                                <span className={styles.slotEnd}>
                                    {slot.isRecommended && (
                                        <Badge tone="brand">{t('scheduleMainContent.recommend')}</Badge>
                                    )}
                                    <span className={styles.slotCount}>
                                        {t('scheduleMainContent.voteCount', { count: slot.voteCount })}
                                        {' · '}
                                        {t('scheduleMainContent.proposerCount', { count: slot.availableCount })}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                    {sortedTimeSlots.length > 5 && (
                        <button type="button" className={styles.more} onClick={onVote}>
                            {t('scheduleMainContent.moreSlots', { count: sortedTimeSlots.length - 5 })}
                        </button>
                    )}
                    <div className={styles.action}>
                        <Button variant="brand" size="lg" block onClick={onVote}>
                            {t('scheduleMainContent.vote')}
                        </Button>
                    </div>
                </Section>
            )}

            {/* AI 추천 */}
            {aiRecommendations && aiRecommendations.length > 0 && (
                <Section divided>
                    <SectionHeader
                        title={t('scheduleMainContent.aiTitle')}
                        sub={t('scheduleMainContent.aiDesc')}
                    />
                    <ul className={styles.slots}>
                        {aiRecommendations.slice(0, 3).map((rec: any, index: number) => {
                            const startDate = new Date(rec.startDateTime);
                            const endDate = new Date(rec.endDateTime);
                            /* 날짜만 뽑을 때 toISOString() 을 쓰면 KST 오전 0~9시가 전날로
                               밀립니다. 바로 아래 시각은 로컬이라 날짜와 시각이 어긋났습니다. */
                            const day = toLocalISOString(startDate).slice(0, 10);
                            return (
                                <li key={index} className={styles.slot}>
                                    <span className={styles.slotWhen}>
                                        <Badge tone="weak">{t('scheduleMainContent.recommendN', { n: index + 1 })}</Badge>
                                        {' '}
                                        {formatDate(day)} ({getDayOfWeek(day)}){' '}
                                        {startDate.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })}
                                        {' ~ '}
                                        {endDate.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <span className={styles.slotCount}>
                                        {t('scheduleMainContent.availableCount', { count: rec.availableCount, total: rec.totalCount })}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </Section>
            )}

            {/* 참여자별 */}
            <Section divided>
                <SectionHeader
                    title={t('scheduleMainContent.byParticipant')}
                    sub={t('scheduleMainContent.submittedDone', { count: submittedCount })}
                />
                <div className={styles.people}>
                    {participants
                        .filter(p => p.schedules && p.schedules.length > 0)
                        .map((participant) => (
                            <div key={participant.id} className={styles.person}>
                                <div className={styles.personHead}>
                                    <span className={styles.avatar}>
                                        {participant.avatar && participant.avatar.startsWith('http')
                                            ? <img src={participant.avatar} alt="" />
                                            : (participant.name || '?').charAt(0)}
                                    </span>
                                    <span className={styles.personBody}>
                                        <span className={styles.personName}>{participant.name}</span>
                                        <span className={styles.personMeta}>
                                            {t('scheduleMainContent.proposalCount', { count: participant.schedules.length })}
                                        </span>
                                    </span>
                                </div>
                                <ul className={styles.personSlots}>
                                    {participant.schedules.map((schedule) => (
                                        <li key={schedule.id} className={styles.personSlot}>
                                            {formatDate(schedule.date)} ({getDayOfWeek(schedule.date)}){' '}
                                            {schedule.startTime} ~ {schedule.endTime}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                    {/* 아직 안 낸 사람 — 한 줄로 묶습니다 */}
                    {totalCount - submittedCount > 0 && (
                        <div className={styles.person}>
                            <div className={styles.personHead}>
                                <span className={`${styles.avatar} ${styles.avatarPending}`}>
                                    <IcHourglass size={18} />
                                </span>
                                <span className={styles.personBody}>
                                    <span className={styles.personName}>
                                        {t('scheduleMainContent.pendingCount', { count: totalCount - submittedCount })}
                                    </span>
                                    <span className={styles.personMeta}>
                                        {participants
                                            .filter(p => !p.schedules || p.schedules.length === 0)
                                            .map(p => p.name)
                                            .join(', ')}
                                    </span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Section>

            <Section divided last>
                <p className={styles.info}>{t('scheduleMainContent.infoBox')}</p>
            </Section>

            <ButtonFooter>
                <Button variant="primary" size="lg" block onClick={onAddSchedule}>
                    {t('scheduleMainContent.inputMySchedule')}
                </Button>
            </ButtonFooter>
        </>
    );
};
